<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { Popup } from "vant";
import { useStory } from "@/stores/story";
import { useStoryBook } from "@/stores/story-book";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import { storeToRefs } from "pinia";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import { onMounted } from "vue";
import { Story, StoryBook } from "@/types/entity";
// Props & Emit
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  selectChapterId: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:show",
  "confirm",
  "update:selectChapterId",
  "update:loading",
]);

// State
const selectedStory = ref<Story | null>(null);
const storyStore = useStory();
const isShowStoryBook = ref(true);
const store = useStoryBook();
const { getStoryEditList } = storeToRefs(storyStore);
const validStoryList = computed<Story[]>(() =>
  getStoryEditList.value.filter((item) => item.description)
);

const { allStoryBookList } = storeToRefs(store);

const storyBookList = computed<StoryBook[]>(() =>
  allStoryBookList.value.filter((item) => item.description)
);

const clickStoryBook = async (id: number) => {
  emit("update:loading", true);
  isShowStoryBook.value = false;
  try {
    await storyStore.getEditStoryList(id);
  } catch (e) {}
  emit("update:loading", false);
};

const clickClose = () => {
  if (!isShowStoryBook.value) {
    isShowStoryBook.value = true;
  } else {
    emit("update:show", false);
  }
};

// Lifecycle

// EventHandler
const onSelect = (story: Story) => {
  emit("update:selectChapterId", story.id!);
  selectedStory.value = story;
};

const clickConfirm = async () => {
  emit("confirm", selectedStory.value);
  emit("update:show", false);
};

onMounted(() => {
  store.getStoryBookList();
});
</script>
<template>
  <popup
    position="bottom"
    class="max-h-[80%] overflow-hidden grid grid-rows-[auto_1fr]"
    :show="show"
    @update:show="emit('update:show', $event)"
    round
    teleport="body"
  >
    <div class="navbar h-top-nav border-b">
      <!-- 左 -->
      <div class="navbar-start h-full">
        <button class="btn btn-sm rounded" @click="clickClose">
          {{ isShowStoryBook ? $t("Buttons.cancel") : $t("Buttons.back") }}
        </button>
      </div>
      <!-- 中 -->
      <div class="navbar-center">
        <div class="font-bold">
          {{
            isShowStoryBook
              ? $t("Texts.chooseStory")
              : $t("Texts.selectChapter")
          }}
        </div>
      </div>
      <!-- 右 -->
      <div class="navbar-end">
        <button
          class="btn btn-primary btn-sm rounded"
          :disabled="selectedStory === null"
          @click="clickConfirm"
        >
          {{ $t("Buttons.confirm") }}
        </button>
      </div>
    </div>
    <div class="w-full overflow-hidden flex flex-col relative">
      <div class="overflow-y-auto w-full h-full" ref="scrollableEl">
        <div v-if="loading" class="h-40 flex items-center justify-center">
          <span class="loading"></span>
        </div>
        <template v-else-if="isShowStoryBook">
          <div
            v-show="!loading && storyBookList.length === 0"
            class="h-40 flex flex-col items-center justify-center"
          >
            <div class="text-2xl font-bold text-base-content/50">
              {{ $t("Message.noStoriesYet") }}
            </div>
            <div class="text-base-content/50">
              {{ $t("Message.noStoriesYetDesc") }}
            </div>
          </div>
          <div v-show="!loading && storyBookList.length > 0" class="p-2 pt-0">
            <div
              v-for="item in storyBookList"
              :key="item.id"
              class="relative border border-transparent"
              @click="clickStoryBook(item.id!)"
            >
              <!-- @vue-ignore -->
              <story-list-item class="px-2" :story="item" />
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-show="!loading && validStoryList.length === 0"
            class="h-40 flex flex-col items-center justify-center"
          >
            <div class="text-2xl font-bold text-base-content/50">
              {{ $t("story.noChaptersAvailable") }}
            </div>
            <div class="text-base-content/50">
              {{ $t("story.currentStoryNoChapterCreatedYet") }}
            </div>
          </div>
          <div v-show="!loading && validStoryList.length > 0" class="p-2 pt-0">
            <div
              v-for="item in validStoryList"
              :key="item.id"
              class="relative"
              :class="{
                'border-primary border rounded': selectChapterId === item.id,
                'border border-transparent ': selectChapterId !== item.id,
              }"
              @click="onSelect(item)"
            >
              <story-list-item class="px-2" :story="item" />
              <check-circle-fill-icon
                v-show="selectChapterId === item.id"
                class="text-primary absolute right-1 bottom-1 icon-xl rounded-full"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </popup>
</template>
